<template>
	<v-form ref="form">
		<v-card-text class="pt-0 pb-0">
			<template v-if="!isLoginInProgress && UsersList.length === 0">
				<v-row dense>
					<v-col cols="12">
						<v-btn
							color="primary"
							width="100%"
							large
							style="text-transform: none;"
							@click="onStartLogin()"
							depressed
							:disabled="disabled"
						>
							<img
								:src="require('@/assets/img/BankID_logo_white.svg')"
								height="48"
								width="48"
							/>
							{{$t('BankID.AuthBtnText')}}
						</v-btn>
					</v-col>
				</v-row>
			</template>

			<template v-if="UsersList.length === 0">
				<v-row dense justify="center" v-if="isLoginInProgress">
					<v-col cols="12" class="text-center pb-0">
						<v-progress-circular v-if="!statusText" color="accent" :size="50" indeterminate class="mr-2"/>
						<p v-if="statusText" :aria-label="statusText">{{statusText}}</p>
					</v-col>

					<v-fade-transition hide-on-leave>
						<v-col v-if="QRCodeDataURL" class="pt-0">
							<v-card flat height="220">
								<v-card-text class="pa-0 ma-0 text-center">
									<img v-if="QRCodeDataURL" :src="QRCodeDataURL" style="margin: 0 auto; border: 1px solid gray;" height="220" width="220"/>
								</v-card-text>
							</v-card>
						</v-col>
					</v-fade-transition>
				</v-row>
			</template>

			<template v-else>
				<v-list>
					<v-subheader class="subtitle-1">{{$t('Auth.SelectUserAccount')}}</v-subheader>
					<v-list-item v-for="(item, index) in UsersList" :key="index" @click="onSelectUser(item)">
						<v-list-item-content>
							<v-list-item-title>{{item.Login}}</v-list-item-title>
							<v-list-item-subtitle>{{$formatter.getRollText(item.Roll)}}</v-list-item-subtitle>
						</v-list-item-content>
						<v-list-item-action>
							<Tooltip :text="$t('Auth.SelectUserAccount')">
								<v-btn icon @click="onSelectUser(item)"><v-icon>mdi-login</v-icon></v-btn>
							</Tooltip>
						</v-list-item-action>
					</v-list-item>
				</v-list>
			</template>
		</v-card-text>
		<v-card-actions v-if="isAuthenticating">
			<v-row dense justify="center">
				<v-col cols="12" lg="8" xl="6" class="text-center">
					<v-btn width="100%" v-if="AutoStartToken && QRCodeDataURL" depressed color="primary" class="mb-2" @click="tryStartBankIDApp">{{$t('BankID.StartOnThisDevice')}}</v-btn>
					<v-btn v-if="showCancel" depressed style="width: 100%;" color="secondary" @click="onCancel">{{$t('Cancel')}}</v-btn>
				</v-col>
			</v-row>
		</v-card-actions>
	</v-form>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import isMobile               from 'is-mobile';
import LocalStorage           from '../../../lib/LocalStorage.js';
import Tooltip                from '../../Tooltip.vue';

export default {
	name: 'BankIDLogin',

	props: {
		showCancel: {
			type:    Boolean,
			default: true,
		},

		useUserAccount: {
			type:    Boolean,
			default: false,
		},

		disabled: {
			type:    Boolean,
			default: false,
		},
	},

	watch: {
		isAuthenticating() {
			this.$emit('isAuthenticating', this.isAuthenticating);
		},
	},

	data: () => ({
		isLoginInProgress:       false,
		statusText:              '',
		UsersList:               [],
		QRCodeDataURL:           '',
		currentOrderRef:         null,
		AutoStartToken:          null,
		PersNr:                  undefined,
		startFailedRetryCounter: 0,
	}),

	methods: {
		...mapActions(['setAuthenticated']),

		onSelectUser(user) {
			this.$tryCatch({
				task: async () => {
					const {UserAccount, UserAppSettings, FtgInfo, Auth, HasLicensOption, SalaryYears, HasPlus, HasWebLon, HasWebLonPlus, EnforceBankIDAuth} = await this.$http.post('/bankidauth/selectuser', {
						BankIDToken: user.BankIDToken,
						Login:       user.Login,
					});

					if(UserAppSettings && ['dark', 'white'].includes(UserAppSettings.Theme)) {
						this.$vuetify.theme.dark = UserAppSettings.Theme === 'dark';
					}

					this.setAuthenticated({UserAccount, UserAppSettings, FtgInfo, Auth, HasLicensOption, SalaryYears, HasPlus, HasWebLon, HasWebLonPlus, EnforceBankIDAuth});
					this.$emit('authenticated')
				},
			})
		},

		onCancel() {
			this.$emit('cancelled');
			clearTimeout(this.collectTimer);
			this.UsersList = [];
			this.statusText = '';
			this.isLoginInProgress = false;
			this.QRCodeDataURL = '';
			this.startFailedRetryCounter = 0;

			const currentOrderRef = this.currentOrderRef;

			this.currentOrderRef = null;

			if(currentOrderRef) {
				this.$tryCatch({
					task: async () => {
						await this.$http.delete(`/bankidauth/cancel/${encodeURIComponent(currentOrderRef)}`)
					},
				});
			}
		},

		onStartLogin({resetStartFailedRetryCounter = true} = {}) {
			LocalStorage.set('LoginMethod', 0);
			this.currentOrderRef = null;

			if(resetStartFailedRetryCounter) {
				this.startFailedRetryCounter = 0;
			}

			this.$tryCatch({
				task: async () => {
					this.isLoginInProgress = true;

					const {OrderRef, AutoStartToken} = await this.$http.post('/bankidauth', {
						PersNr: this.useUserAccount ? this.UserAccount.PersNr : undefined,
					});

					this.AutoStartToken = AutoStartToken;

					this.currentOrderRef = OrderRef;

					this.collect(OrderRef);

					if(isMobile({tablet: true})) {
						this.tryStartBankIDApp();
					}
				},
				error: () => {
					this.$emit('startFailed');
					this.isLoginInProgress = false;
				},
			});
		},

		tryStartBankIDApp() {
			const params = [
				`autostarttoken=${this.AutoStartToken}`,
				'redirect=null',
			];

			if(isMobile()) {
				window.location = `https://app.bankid.com/?${params.join('&')}`;
			} else {
				window.location = `bankid:///?${params.join('&')}`;
			}
		},

		collect(orderRef) {
			const collect           = async () => {
				if(this.collectAttempts > 60) {
					this.onCancel();

					return;
				}

				try {
					const {
						UsersList,
						Completed,
						Cancelled,
						Message,
						QRCodeDataURL,
						PendingStart,
						StartFailed,
					} = await this.$http.get(`/bankidauth/collect/${orderRef}`);

					if(Cancelled || Completed || StartFailed) {
						if(StartFailed && this.startFailedRetryCounter < 5) {
							this.startFailedRetryCounter++;
							this.onStartLogin({resetStartFailedRetryCounter: false});

							return;
						} else if(Cancelled || StartFailed) {
							setTimeout(() => {
								this.statusText    = Message;
							}, 7000);
							setTimeout(() => {
								this.isLoginInProgress = false;
							}, 10000);
						} else {
							this.isLoginInProgress = false;
						}
					}

					if(PendingStart && QRCodeDataURL) {
						this.QRCodeDataURL = QRCodeDataURL;
						this.statusText    = this.$t('BankID.ScanQRCode')
					} else {
						this.QRCodeDataURL = '';
						if(!Cancelled) {
							this.statusText    = Message;
						}
					}

					if(Completed) {
						if(UsersList.length === 0) {
							this.$msgError(this.$t('BankID.NoAccountsExistForPersonalIdentityNumber'));
							
							return;
						}

						if(this.useUserAccount) {
							this.onSelectUser(UsersList.find((item) => item.Login === this.UserAccount.Login));
						} else if(UsersList.length === 1 && UsersList[0].Roll === 'PER') {
							this.onSelectUser(UsersList[0]);
						} else {
							this.UsersList = UsersList;
						}
					} else if(!Cancelled) {
						this.collectTimer = setTimeout(collect, 1500);
					}
				} catch{
					this.isLoginInProgress = false;
					clearTimeout(this.collectTimer);
				} finally {
					this.collectAttempts = this.collectAttempts + 1;
				}
			};

			if(this.collectTimer) {
				clearTimeout(this.collectTimer);
			}

			this.collectAttempts = 0;

			collect();
		},
	},

	computed: {
		...mapState(['isMobileApp', 'UserAccount']),

		isAuthenticating() {
			return this.isLoginInProgress || this.UsersList.length > 0;
		},
	},

	components: {
		Tooltip,
	},
}
</script>

<style scoped>

</style>
